'use client';

import { useState, type FormEvent } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  Input,
  Label,
} from '@hr/ui';

type Status = 'idle' | 'submitting' | 'success' | 'error' | 'duplicate';

export function WaitlistDialog({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus('submitting');

    try {
      const res = await fetch('/api/v1/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim() || undefined,
        }),
      });

      if (res.status === 409) {
        setStatus('duplicate');
        return;
      }

      if (!res.ok) throw new Error('Request failed');

      setStatus('success');
    } catch {
      setStatus('error');
    }
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      // Reset form after close animation
      setTimeout(() => {
        setStatus('idle');
        setEmail('');
        setName('');
      }, 200);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        {status === 'success' ? (
          <div className="py-6 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <DialogHeader className="items-center">
              <DialogTitle>You&apos;re on the list</DialogTitle>
              <DialogDescription>
                We&apos;ll reach out when early access opens. Keep an eye on your inbox.
              </DialogDescription>
            </DialogHeader>
            <Button
              className="mt-6"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Request Early Access</DialogTitle>
              <DialogDescription>
                Be among the first to try RevoluteHR when we launch.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="waitlist-email">
                  Work email <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="waitlist-email"
                  type="email"
                  placeholder="you@company.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={status === 'submitting'}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="waitlist-name">Name</Label>
                <Input
                  id="waitlist-name"
                  type="text"
                  placeholder="Optional"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={status === 'submitting'}
                />
              </div>

              {status === 'duplicate' && (
                <p className="text-sm text-amber-600">
                  This email is already on the waitlist. We&apos;ll be in touch!
                </p>
              )}
              {status === 'error' && (
                <p className="text-sm text-destructive">
                  Something went wrong. Please try again.
                </p>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? 'Joining...' : 'Join the Waitlist'}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                No spam. We&apos;ll only email you about early access.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
